type ClassNamesArg = string | undefined | null | Record<string, boolean> | Array<string | undefined | null>

/**
 * Combines multiple arguments into a single string of class names,
 * converting camelCase values to kebab-case and filtering out falsey values.
 *
 *
 * Examples:
 *
 * ```
 * classNames('btn', 'btnPrimary')
 * // Output: 'btn btn-primary'
 *
 * classNames('btn', undefined, null, 'btnPrimary')
 * // Output: 'btn btn-primary'
 *
 * classNames('btn-primary', { 'isActive': true, 'isDisabled': false })
 * // Output: 'btn-primary is-active'
 *
 * classNames(['btn', undefined, 'btnPrimary'], { 'isHidden': true })
 * // Output: 'btn btn-primary is-hidden'
 * ```
 * @param args - An array of arguments to process.
 *               Each argument can be:
 *               - A string (converted to kebab-case)
 *               - Undefined or null (ignored)
 *               - An object where keys are class names and values indicate inclusion (truthy values are included)
 *               - An array of strings or undefined/null values (filtered and processed recursively)
 *
 * @returns A single string of space-separated class names in kebab-case.
 */
export const classNames = (...args: ClassNamesArg[]) => {
    const toKebabCase = (str: string) => str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()
    return args
        .flatMap(arg => {
            // Handle string values and filter out undefined or null
            if (typeof arg === 'string') return toKebabCase(arg)

            // Handle arrays
            if (Array.isArray(arg)) return arg.filter(Boolean).map(toKebabCase)

            // Handle objects (keep keys with truthy values)
            if (typeof arg === 'object' && arg !== null) {
                return Object.keys(arg)
                    .filter(key => arg[key])
                    .map(toKebabCase)
            }

            return []
        })
        .filter(Boolean)
        .join(' ')
        .trim()
}

/**
 * Combines multiple arguments into a single string of class names,
 * converting camelCase values to kebab-case and filtering out falsey values.
 *
 *
 * Examples:
 *
 * ```
 * classNames('btn', 'btnPrimary')
 * // Output: 'btn btn-primary'
 *
 * classNames('btn', undefined, null, 'btnPrimary')
 * // Output: 'btn btn-primary'
 *
 * classNames('btn-primary', { 'isActive': true, 'isDisabled': false })
 * // Output: 'btn-primary is-active'
 *
 * classNames(['btn', undefined, 'btnPrimary'], { 'isHidden': true })
 * // Output: 'btn btn-primary is-hidden'
 *```
 * @param args - An array of arguments to process.
 *               Each argument can be:
 *               - A string (converted to kebab-case)
 *               - Undefined or null (ignored)
 *               - An object where keys are class names and values indicate inclusion (truthy values are included)
 *               - An array of strings or undefined/null values (filtered and processed recursively)
 *
 * @returns A single string of space-separated class names in kebab-case.
 */
export const cx = classNames
